import React, { useEffect, useLayoutEffect, useCallback } from 'react';

import TvGuideContext from '../utils/tv-guide-context';
import TvGuideView from './tv-guide-view';
import { TvGuideDayViewProvider } from '../utils/tv-guide-day-view-context';
import TvGuideDialogCalendar from './tv-guide-dialog-calendar';
import TvGuideDialogFavorites from './tv-guide-dialog-favorites';
import TvGuideSkeleton from './tv-guide-skeleton';
import NetworkErrorDialog from '@smart-tv/components/error-dialog/network-error-dialog';
import useNetwork from '@smart-tv/hooks/use-network/use-network';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  headerActiveAtom,
  topLevelPageReadyAtom,
  tvGuideRecordingStatusUpdatesAtom
} from '@smart-tv/atoms';
import {
  links,
  useNavigateToHeaderDestination
} from '@smart-tv/components/header/header-navigation';
import { HEADER_TVGUIDE } from '@smart-tv/components/header/header-helpers';
import useGetChannels from '../utils/use-get-channels';

// Derived from links instead of hardcoded, since its position can change (e.g. Søg).
const TVGUIDE_INDEX = links.findIndex((link) => link.focusKey === HEADER_TVGUIDE);

export const TvGuide: React.FC = () => {
  const {
    channels,
    setChannels,
    pendingScrollToLive,
    scrollToCurrentTimeAction,
    setSavedTimelinePosition
  } = React.useContext(TvGuideContext);

  const { getChannels, selectedFavoritesList, date } = useGetChannels();

  const {
    checkAndUpdateNetwork,
    isOffline,
    setIsOffline,
    isNetworkDialogSuppressed,
    setIsNetworkDialogSuppressed
  } = useNetwork();
  const activeItemInHeader = useAtomValue(headerActiveAtom);
  const navigateToHeaderDestination = useNavigateToHeaderDestination();
  const setHeaderActive = useSetAtom(headerActiveAtom);
  const setTopLevelPageReady = useSetAtom(topLevelPageReadyAtom);
  const setTvGuideRecordingStatusUpdates = useSetAtom(tvGuideRecordingStatusUpdatesAtom);

  // Clear saved position and channels when entering/leaving TvGuide
  // This ensures fresh scroll to current time on return
  useLayoutEffect(() => {
    setSavedTimelinePosition(null);
    return () => {
      setChannels([]);
      setTvGuideRecordingStatusUpdates({ programs: {}, series: {} });
    };
  }, [setSavedTimelinePosition, setChannels, setTvGuideRecordingStatusUpdates]);

  const handleNetworkErrorDialogCloseAction = useCallback(() => {
    //navigate back to TvGuide
    setHeaderActive(TVGUIDE_INDEX);
    setIsOffline(false);
    setIsNetworkDialogSuppressed(true);
  }, [setHeaderActive, setIsOffline, setIsNetworkDialogSuppressed]);

  const handleNetworkErrorDialogRetryAction = useCallback(async () => {
    if (pendingScrollToLive) {
      await scrollToCurrentTimeAction?.();
      return;
    }
    const hasNetwork = await checkAndUpdateNetwork();
    if (hasNetwork) {
      if (
        activeItemInHeader !== null &&
        activeItemInHeader !== undefined &&
        activeItemInHeader !== TVGUIDE_INDEX
      ) {
        navigateToHeaderDestination(activeItemInHeader);
      } else {
        getChannels();
      }
    }
  }, [
    getChannels,
    checkAndUpdateNetwork,
    activeItemInHeader,
    scrollToCurrentTimeAction,
    navigateToHeaderDestination,
    pendingScrollToLive
  ]);

  useEffect(() => {
    if (selectedFavoritesList) {
      getChannels();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFavoritesList?.id, selectedFavoritesList?.sids?.join(','), date]);

  useEffect(() => {
    if (channels?.length) setTopLevelPageReady(true);
  }, [channels, setTopLevelPageReady]);

  if (!channels?.length) return <TvGuideSkeleton />;
  return (
    <React.Fragment>
      <TvGuideDayViewProvider>
        <TvGuideView channels={channels} getChannels={getChannels} />
        {isOffline && !isNetworkDialogSuppressed && (
          <NetworkErrorDialog
            onTryAgain={handleNetworkErrorDialogRetryAction}
            onClose={handleNetworkErrorDialogCloseAction}
          />
        )}
        <TvGuideDialogCalendar />
        <TvGuideDialogFavorites />
      </TvGuideDayViewProvider>
    </React.Fragment>
  );
};

export default TvGuide;
